import { type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { ProjectHealthCard } from '../../dashboard/components/project-health-card';
import { type ReportRange } from '../api';
import { AgingWipList } from './aging-wip-list';
import { ObjectiveTrace } from './objective-trace';
import { RiskBurndownChart } from './risk-burndown-chart';
import { VelocityChart } from './velocity-chart';

/**
 * Tổng hợp cấp dự án — APM-REPORT-b.
 *
 * ## Tiêu chí giữ lại: câu hỏi có đúng tầm dự án không
 *
 * Tab Báo cáo có mười lăm biểu đồ, và phần lớn trả lời câu hỏi **của một
 * khoảng ngắn**: throughput tuần này, cycle time bốn tuần qua, luồng công việc
 * trong sprint. Đọc chúng ở tầng dự án là đọc một con số trung bình trên sáu
 * tháng, và trung bình sáu tháng của một đội có nhịp thay đổi thì không nói lên
 * điều gì.
 *
 * Năm thứ ở đây đều trả lời được câu hỏi "dự án này đang thế nào":
 *
 * - **Sức khoẻ** — kết luận RAG kèm ba tín hiệu đã dùng để kết luận.
 * - **Velocity** — nhịp giao hàng qua **nhiều** sprint; nó vốn là biểu đồ dài hạn.
 * - **Rủi ro còn lại** — rủi ro thuộc dự án, không thuộc sprint nào.
 * - **Cây mục tiêu** — mục tiêu sống qua nhiều sprint.
 * - **Việc đang ì** — nói về **hiện tại**, nên không có phiên bản "của sprint 5".
 *
 * ## ⚠️ Không có `SprintFilter` ở đây, và đó là chủ đích
 *
 * Cả năm thứ trên hoặc là cấp dự án, hoặc là "ngay bây giờ". Thêm một ô chọn
 * sprint vào đây là hứa một phép lọc mà bốn trong năm biểu đồ không thực hiện
 * được — và một bộ lọc không có tác dụng còn tệ hơn không có bộ lọc, vì người
 * dùng tin rằng nó đã lọc.
 */
export function SummaryPanel({
  projectId,
  range,
  asOf,
}: {
  readonly projectId: string;
  readonly range: ReportRange;
  readonly asOf: string | undefined;
}): ReactNode {
  const { t } = useTranslation();

  return (
    <div className="flex flex-col gap-4">
      <p className="text-xs text-muted-foreground">{t('reports.summaryHelp')}</p>

      <ProjectHealthCard projectId={projectId} asOf={asOf} />

      <div className="grid gap-4 xl:grid-cols-2">
        <VelocityChart projectId={projectId} />
        <RiskBurndownChart projectId={projectId} range={range} />
        <ObjectiveTrace projectId={projectId} asOf={asOf} />
        <AgingWipList projectId={projectId} asOf={asOf} />
      </div>
    </div>
  );
}
